import { useCallback, useContext, useState } from "react";
import {
    Button,
    FormLayout,
    Icon,
    InlineGrid,
    LegacyStack,
    RadioButton,
    Select,
    TextField,
    Thumbnail,
} from "@shopify/polaris";
import styles from "./CartFormCards.module.css";
import { ProductObj, VariantsObj } from "../../../Bundles/Types";
import { CollectionIcon, SearchIcon } from "@shopify/polaris-icons";
import CloseIcon from "../../CartIcons/CloseIcon";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import NewCustomColorPicker from "../../../../components/NewCustomColorPicker/NewCustomColorPicker";

const MetalBirdForm = ({ fields, dispatchCartConfig }: { fields: any; dispatchCartConfig: any }) => {
    const { currency } = useContext(AppConfigContext);

    const {
        metal_bird_config: {
            free_gifts,
            product_selection_type,
            products,
            collections,
            qualifier_type,
            count,
            unreached_title,
            reached_title,
            text_color,
            text_background_color,
            card_background_color,
            inline_btn_bg_color,
            inline_btn_text_color,
            outline_btn_bg_color,
            outline_btn_text_color,
        },
    } = fields;

    const [searchQuery, setSearchQuery] = useState(""); // for products or collections selector
    const [productSearchQuery, setProductSearchQuery] = useState(""); // for free gift selector

    const handleProductSearch = async (updateField: any, keyName: "free_gifts" | "products") => {
        const preSelected = updateField?.value?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: VariantsObj) => ({
                id: variant?.id,
            })),
        }));

        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: keyName === "free_gifts" ? productSearchQuery : searchQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: false,
            },
        });
        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.map((p: any) => {
                productsArr.push({
                    id: p?.id,
                    images: p?.images,
                    title: p?.title,
                    handle: p?.handle,
                    quantity: 1,
                    maxProductStock: p?.totalInventory,
                    selectedVariants: [],
                    subscriptions: [],
                    oneTimePurchase: true,
                    isSubscribed: false,
                    variants: p?.variants?.map((variant: VariantsObj) => ({
                        image: { ...variant.image },
                        id: variant?.id,
                        displayName: variant.displayName,
                        title: variant?.title?.toLowerCase() == "default title" ? p?.title : variant?.title,
                        price: variant?.price,
                        quantity: 1,
                        selectedOptions: variant?.selectedOptions,
                        copyPrice: variant.price,
                        compareAtPrice: variant?.compareAtPrice ? variant.compareAtPrice : variant.price,
                    })),
                    options: p?.options,
                });
            });
            updateField.onChange(productsArr);
            dispatchCartConfig({
                payload: {
                    [keyName]: productsArr,
                },
                isComplex: true,
                key: "metal_bird_config",
            });
            setProductSearchQuery("");
            setSearchQuery("");
        }
    };

    const handleCollectionSearch = async () => {
        const preSelected = collections?.value?.map((product: ProductObj) => ({
            id: product?.id,
        }));

        const selected = await shopify.resourcePicker({
            type: "collection",
            multiple: true,
            query: searchQuery,
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: false,
            },
        });
        if (selected) {
            const collectionArr: any[] = [];
            selected?.map((c: any) => {
                collectionArr.push({
                    id: c?.id,
                    image: c?.image?.originalSrc || "",
                    title: c.title,
                });
            });
            collections.onChange(collectionArr);
            dispatchCartConfig({
                payload: {
                    collections: collectionArr,
                },
                isComplex: true,
                key: "metal_bird_config",
            });
            setSearchQuery("");
        }
    };

    const removeProducts = (pId: string = "", updateField: any, keyName: "free_gifts" | "products") => {
        const temp = [...updateField.value];
        let idx: number | undefined = -1;
        idx = temp.findIndex((p: any) => p.id == pId);

        if (idx >= 0) {
            temp.splice(idx, 1);
            updateField.onChange(temp);
            dispatchCartConfig({
                payload: {
                    [keyName]: temp,
                },
                isComplex: true,
                key: "metal_bird_config",
            });
        }
    };

    const removeCollection = (cId: string = "") => {
        const temp = [...collections.value];
        let idx: number | undefined = -1;
        idx = temp.findIndex((c: any) => c.id == cId);

        if (idx >= 0) {
            temp.splice(idx, 1);
            collections.onChange(temp);
            dispatchCartConfig({
                payload: {
                    collections: temp,
                },
                isComplex: true,
                key: "metal_bird_config",
            });
        }
    };

    const handleChange = useCallback((_: boolean, newValue: string) => {
        product_selection_type.onChange(newValue);
        dispatchCartConfig({
            payload: {
                product_selection_type: newValue,
            },
            isComplex: true,
            key: "metal_bird_config",
        });
        if (newValue == "products") {
            collections.onChange([]);
            dispatchCartConfig({
                payload: {
                    collections: [],
                },
                isComplex: true,
                key: "metal_bird_config",
            });
        } else {
            products.onChange([]);
            dispatchCartConfig({
                payload: {
                    products: [],
                },
                isComplex: true,
                key: "metal_bird_config",
            });
        }
    }, []);

    return (
        <div>
            <>
                <div className={styles.designCardWrapper}>
                    <FormLayout>
                        <LegacyStack vertical spacing="tight">
                            <TextField
                                label="Unreached Title"
                                placeholder="text"
                                autoComplete="off"
                                {...unreached_title}
                                onBlur={unreached_title?.onBlur}
                                maxLength={100}
                                showCharacterCount
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            unreached_title: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                    unreached_title.onChange(val);
                                }}
                            />
                            <TextField
                                label="Reached Title"
                                placeholder="text"
                                autoComplete="off"
                                {...reached_title}
                                onBlur={reached_title?.onBlur}
                                showCharacterCount
                                maxLength={100}
                                onChange={(val) => {
                                    dispatchCartConfig({
                                        payload: {
                                            reached_title: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                    reached_title.onChange(val);
                                }}
                            />
                        </LegacyStack>

                        <InlineGrid columns={2} gap={"400"}>
                            <NewCustomColorPicker
                                hexColor={text_color.value}
                                setHexColor={(val) => {
                                    text_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: {
                                            text_color: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                }}
                                label={"Text Color"}
                                props={{
                                    ...text_color,
                                    onBlur: () => text_color?.runValidation(),
                                }}
                            />
                            <NewCustomColorPicker
                                hexColor={text_background_color.value}
                                setHexColor={(val) => {
                                    text_background_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: {
                                            text_background_color: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                }}
                                label={"Text Background Color"}
                                props={{
                                    ...text_background_color,
                                    onBlur: () => text_background_color?.runValidation(),
                                }}
                            />
                            <NewCustomColorPicker
                                hexColor={inline_btn_text_color.value}
                                setHexColor={(val) => {
                                    inline_btn_text_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: {
                                            inline_btn_text_color: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                }}
                                label={"Inline Button Text Color"}
                                props={{
                                    ...inline_btn_text_color,
                                    onBlur: () => inline_btn_text_color?.runValidation(),
                                }}
                            />
                            <NewCustomColorPicker
                                hexColor={inline_btn_bg_color.value}
                                setHexColor={(val) => {
                                    inline_btn_bg_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: {
                                            inline_btn_bg_color: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                }}
                                label={"Inline Button Background Color"}
                                props={{
                                    ...inline_btn_bg_color,
                                    onBlur: () => inline_btn_bg_color?.runValidation(),
                                }}
                            />
                            <NewCustomColorPicker
                                hexColor={outline_btn_text_color.value}
                                setHexColor={(val) => {
                                    outline_btn_text_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: {
                                            outline_btn_text_color: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                }}
                                label={"Outline Button Text Color"}
                                props={{
                                    ...outline_btn_text_color,
                                    onBlur: () => outline_btn_text_color?.runValidation(),
                                }}
                            />
                            <NewCustomColorPicker
                                hexColor={outline_btn_bg_color.value}
                                setHexColor={(val) => {
                                    outline_btn_bg_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: {
                                            outline_btn_bg_color: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                }}
                                label={"Outline Button Background Color"}
                                props={{
                                    ...outline_btn_bg_color,
                                    onBlur: () => outline_btn_bg_color?.runValidation(),
                                }}
                            />
                            <NewCustomColorPicker
                                hexColor={card_background_color.value}
                                setHexColor={(val) => {
                                    card_background_color.onChange(val);
                                    dispatchCartConfig({
                                        payload: {
                                            card_background_color: val,
                                        },
                                        isComplex: true,
                                        key: "metal_bird_config",
                                    });
                                }}
                                label={"Card Background Color"}
                                props={{
                                    ...card_background_color,
                                    onBlur: () => card_background_color?.runValidation(),
                                }}
                            />
                        </InlineGrid>

                        <LegacyStack vertical spacing="none">
                            <div className={styles.cardLabel}>Trigger Products Type</div>
                            <RadioButton
                                label="Specific Products"
                                checked={product_selection_type.value === "products"}
                                id="products"
                                name="product_selection_type"
                                onChange={handleChange}
                            />
                            <RadioButton
                                label="Collection"
                                id="collections"
                                name="product_selection_type"
                                checked={product_selection_type.value === "collections"}
                                onChange={handleChange}
                            />
                        </LegacyStack>

                        {/* ------------------------------------------------------------------------------------------ */}

                        <div>
                            <div className={styles.cardLabel}>
                                Choose Trigger {product_selection_type.value == "products" ? "Products" : "Collections"}
                            </div>
                            <div
                                style={{
                                    backgroundColor: "#f9f9f9",
                                    borderRadius: 8,
                                    padding: 15,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 10,
                                }}
                            >
                                <TextField
                                    label={"Search Product"}
                                    labelHidden
                                    placeholder="Search"
                                    autoComplete={"off"}
                                    value={searchQuery}
                                    onChange={(val) => {
                                        setSearchQuery(val);
                                    }}
                                    prefix={<Icon source={SearchIcon} />}
                                    size="slim"
                                    connectedRight={
                                        <div style={{ marginLeft: "8px" }}>
                                            <Button
                                                onClick={() => {
                                                    if (product_selection_type.value == "products") {
                                                        handleProductSearch(products, "products");
                                                    } else {
                                                        handleCollectionSearch();
                                                    }
                                                }}
                                                size="slim"
                                            >
                                                Browse
                                            </Button>
                                        </div>
                                    }
                                />

                                {product_selection_type.value == "products" && products?.value?.length > 0 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 5,
                                            border: "1px solid rgba(227, 227, 227, 1)",
                                            borderRadius: 12,
                                            backgroundColor: "#fff",
                                            padding: 10,
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                        }}
                                    >
                                        {products?.value?.map((p: any, idx: number) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 10,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-start",
                                                        alignItems: "center",
                                                        gap: 10,
                                                    }}
                                                >
                                                    <div>
                                                        <Thumbnail
                                                            source={p?.images?.[0]?.originalSrc || CollectionIcon}
                                                            alt={p?.title}
                                                            size={"small"}
                                                        />
                                                    </div>
                                                    <p>{p?.title}</p>
                                                </div>

                                                <div
                                                    onClick={() => {
                                                        removeProducts(p.id, products, "products");
                                                    }}
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <CloseIcon />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {product_selection_type.value == "collections" && collections?.value?.length > 0 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 5,
                                            border: "1px solid rgba(227, 227, 227, 1)",
                                            borderRadius: 12,
                                            backgroundColor: "#fff",
                                            padding: 10,
                                        }}
                                    >
                                        {collections?.value?.map((c: any, idx: number) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 10,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-start",
                                                        alignItems: "center",
                                                        gap: 10,
                                                    }}
                                                >
                                                    <div>
                                                        <Thumbnail
                                                            source={c?.image || CollectionIcon}
                                                            alt={c?.title}
                                                            size={"small"}
                                                        />
                                                    </div>
                                                    <p>{c?.title}</p>
                                                </div>

                                                <div
                                                    onClick={() => {
                                                        removeCollection(c.id);
                                                    }}
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <CloseIcon />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ------------------------------------------------------------------------------------------ */}

                        <div>
                            <div className={styles.cardLabel}>Qualifiers</div>
                            <InlineGrid columns={2} gap={"400"}>
                                <Select
                                    label="Qualifier"
                                    options={[
                                        { label: "Minimum Quantity", value: "min_quantity" },
                                        { label: "Minimum Spend", value: "min_price" },
                                    ]}
                                    onBlur={qualifier_type?.onBlur}
                                    value={qualifier_type.value}
                                    onChange={(value) => {
                                        dispatchCartConfig({
                                            payload: {
                                                qualifier_type: value,
                                            },
                                            isComplex: true,
                                            key: "metal_bird_config",
                                        });
                                        qualifier_type.onChange(value);
                                    }}
                                />

                                <TextField
                                    label={"Qualifier Count"}
                                    placeholder="5"
                                    autoComplete={"off"}
                                    {...count}
                                    type="number"
                                    prefix={qualifier_type.value == "min_price" ? currency : ""}
                                    onChange={(val) => {
                                        dispatchCartConfig({
                                            payload: {
                                                count: +val,
                                            },
                                            isComplex: true,
                                            key: "metal_bird_config",
                                        });
                                        count.onChange(val);
                                    }}
                                />
                            </InlineGrid>
                        </div>

                        {/* ------------------------------------------------------------------------------------------ */}

                        <div>
                            <div className={styles.cardLabel}>Choose Free Gifts</div>
                            <div
                                style={{
                                    backgroundColor: "#f9f9f9",
                                    borderRadius: 8,
                                    padding: 15,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 10,
                                }}
                            >
                                <TextField
                                    label={"Search Product"}
                                    labelHidden
                                    placeholder="Search"
                                    autoComplete={"off"}
                                    value={productSearchQuery}
                                    onChange={(val) => {
                                        setProductSearchQuery(val);
                                    }}
                                    prefix={<Icon source={SearchIcon} />}
                                    size="slim"
                                    connectedRight={
                                        <div style={{ marginLeft: "8px" }}>
                                            <Button
                                                onClick={() => handleProductSearch(free_gifts, "free_gifts")}
                                                size="slim"
                                            >
                                                Browse
                                            </Button>
                                        </div>
                                    }
                                />

                                {free_gifts?.value?.length > 0 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 5,
                                            border: "1px solid rgba(227, 227, 227, 1)",
                                            borderRadius: 12,
                                            backgroundColor: "#fff",
                                            padding: 10,
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                        }}
                                    >
                                        {free_gifts?.value?.map((p: any, idx: number) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 10,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-start",
                                                        alignItems: "center",
                                                        gap: 10,
                                                    }}
                                                >
                                                    <div>
                                                        <Thumbnail
                                                            source={p?.images?.[0]?.originalSrc || CollectionIcon}
                                                            alt={p?.title}
                                                            size={"small"}
                                                        />
                                                    </div>
                                                    <p>{p?.title}</p>
                                                </div>

                                                <div
                                                    onClick={() => {
                                                        removeProducts(p.id, free_gifts, "free_gifts");
                                                    }}
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <CloseIcon />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </FormLayout>
                </div>
            </>
        </div>
    );
};

export default MetalBirdForm;
